/**
 * Where this VIN's cohort trades — the replacement for the retired
 * `location_and_channel_*_vehicle_insights` JSON blobs.
 *
 * Three sections from one `GET /vehicles/{vin}/cohort-insights` call:
 *   1. Price positioning — this unit's floor vs what the cohort's buyers actually pay
 *   2. Channel fit       — which channel the cohort trades in
 *   3. Where to list it  — ranked venues by cohort buyer depth
 *
 * The cohort is make + model + year band, the same definition `buyer_coverage`
 * uses, so the headline buyer count agrees with the scalar on the pop-out.
 *
 * The Client pop-out splits these sections across two cards, so it fetches once
 * via `useVinCohortInsights` and feeds the result in through `data`.
 */
import React from 'react';
import { PriceLadder, type PriceMarker, type PriceLadderBand } from './PriceLadder';
import { parsePriceRange } from './priceRange';
import { PRICE_FIELD_HELP, VMA_FIELD_HELP } from './vmaFieldHelp';
import { useVinCohortInsights, type VinCohortInsights } from './useVinCohortInsights';
import './VinCohortInsightsCard.css';

export type CohortSection = 'price' | 'channels' | 'venues';

const ALL_SECTIONS: CohortSection[] = ['price', 'channels', 'venues'];

/** Every price the ladder can plot, so callers pass one object instead of ten props. */
export interface VinPricing {
  floorPrice?: number | null;
  buyNowPrice?: number | null;
  baseMmr?: number | null;
  adjMmr?: number | null;
  mmrRange?: string | null;
  retailValue?: number | null;
  retailValueRange?: string | null;
  retailListPrice?: number | null;
  retailRmi?: number | null;
  retailAvgListPrice?: number | null;
  retailSpread?: number | null;
  retailRetention?: number | null;
}

interface VinCohortInsightsCardProps {
  vin: string;
  /** This unit's own pricing, for the ladder and the positioning verdict. */
  pricing?: VinPricing;
  /** Where the unit is listed today, so the venue table can show the delta. */
  currentAuction?: string | null;
  /** Which sections to render. Defaults to all three. */
  sections?: CohortSection[];
  /** Pre-fetched payload. When supplied the card does not fetch for itself. */
  insights?: VinCohortInsights;
}

const fmtMoney = (v: number | null | undefined): string =>
  v == null ? '–' : `$${Math.round(v).toLocaleString()}`;

const fmtInt = (v: number | null | undefined): string =>
  v == null ? '–' : Math.round(v).toLocaleString();

const fmtRetention = (v: number | null | undefined): string =>
  v == null ? '–' : `${Math.round(v * 100)}%`;

const fmtDistance = (v: number | null | undefined): string =>
  v == null ? '–' : `${Math.round(v)} mi`;

export const VinCohortInsightsCard: React.FC<VinCohortInsightsCardProps> = ({
  vin,
  pricing,
  currentAuction,
  sections = ALL_SECTIONS,
  insights,
}) => {
  const own = useVinCohortInsights(vin, insights === undefined);
  const { data, isLoading, error } = insights ?? own;
  const show = (s: CohortSection) => sections.includes(s);

  if (isLoading) return <div className="vci-state">Loading cohort insights…</div>;
  if (error) return <div className="vci-state vci-state--error">{error}</div>;

  const cohort = data?.cohort;
  const venues = data?.venues || [];
  const channels = data?.channels || [];

  // A cohort can have real buyers but no venue/channel rows (all activity missing an
  // auction). Price positioning is still worth showing, so only bail when there's no
  // cohort at all.
  if (!cohort) {
    return <div className="vci-state">No cohort activity found for this vehicle.</div>;
  }

  const avgPaid = cohort.avg_paid ?? null;
  const p = pricing ?? {};
  // Positive = this unit's floor sits above what the cohort's buyers have been paying.
  const floorGapPct =
    p.floorPrice != null && avgPaid != null && avgPaid > 0
      ? ((p.floorPrice - avgPaid) / avgPaid) * 100
      : null;

  const ladderMarkers: PriceMarker[] = [
    { key: 'floor', label: 'Floor', value: p.floorPrice, tone: 'ask', hint: PRICE_FIELD_HELP.floor_price },
    { key: 'buy_now', label: 'Buy Now', value: p.buyNowPrice, tone: 'ask', hint: PRICE_FIELD_HELP.buy_now_price },
    { key: 'base_mmr', label: 'Base MMR', value: p.baseMmr, tone: 'market', hint: PRICE_FIELD_HELP.base_mmr },
    { key: 'adj_mmr', label: 'Adj MMR', value: p.adjMmr, tone: 'market', hint: PRICE_FIELD_HELP.adjusted_mmr },
    { key: 'cohort_paid', label: 'Cohort paid', value: avgPaid, tone: 'cohort', hint: PRICE_FIELD_HELP.cohort_avg_paid },
    { key: 'retail_value', label: 'Retail value', value: p.retailValue, tone: 'retail', hint: PRICE_FIELD_HELP.retail_value },
    { key: 'retail_list', label: 'Retail list', value: p.retailListPrice, tone: 'retail', hint: PRICE_FIELD_HELP.retail_list_price },
    { key: 'retail_rmi', label: 'Market RMI', value: p.retailRmi, tone: 'retail', hint: PRICE_FIELD_HELP.retail_rmi_ladder },
    { key: 'retail_avg_list', label: 'Market avg list', value: p.retailAvgListPrice, tone: 'retail', hint: PRICE_FIELD_HELP.retail_avg_list_ladder },
  ];

  const mmrBand = parsePriceRange(p.mmrRange);
  const retailBand = parsePriceRange(p.retailValueRange);
  const ladderBands: PriceLadderBand[] = [
    ...(mmrBand
      ? [{ key: 'mmr', ...mmrBand, tone: 'wholesale' as const, label: `MMR range ${p.mmrRange}` }]
      : []),
    ...(retailBand
      ? [{ key: 'retail', ...retailBand, tone: 'retail' as const, label: `Retail value range ${p.retailValueRange}` }]
      : []),
  ];

  return (
    <div className="vci">
      {show('price') && (
        <p className="vci-cohort-line">
          <strong>{fmtInt(cohort.buyers)}</strong>
          {` buyers active in this cohort`}
          {cohort.cohort_year
            ? ` (${cohort.make} ${cohort.model} · ${cohort.cohort_year})`
            : ''}
          <span className="vci-subtle">{` · matched on ${cohort.matched_on}`}</span>
        </p>
      )}

      {show('price') && (
        <section className="vci-section">
          <h4 className="vci-heading">
            Price positioning
            <span className="vci-subtle"> · hover any label for what it means</span>
          </h4>
          <PriceLadder
            markers={ladderMarkers}
            bands={ladderBands}
            legend={[
              { tone: 'ask', label: 'Your ask' },
              { tone: 'market', label: 'Wholesale (MMR)' },
              { tone: 'cohort', label: 'Cohort actually paid' },
              { tone: 'retail', label: 'Retail' },
            ]}
          />

          {/* Ratios and gaps have no place on a money axis, so they sit under it. */}
          <div className="vci-stat-row">
            <div className="vci-stat" title={VMA_FIELD_HELP.retail_spread}>
              <span className="vci-stat-label">Retail spread</span>
              <span className="vci-stat-value">{fmtMoney(p.retailSpread)}</span>
              <span className="vci-stat-note">retail vs wholesale gap</span>
            </div>
            <div className="vci-stat" title={VMA_FIELD_HELP.retail_retention}>
              <span className="vci-stat-label">Retail retention</span>
              <span className="vci-stat-value">{fmtRetention(p.retailRetention)}</span>
              <span className="vci-stat-note">holds value at retail</span>
            </div>
            <div className="vci-stat" title={PRICE_FIELD_HELP.cohort_avg_paid}>
              <span className="vci-stat-label">Cohort retention</span>
              <span className="vci-stat-value">{fmtRetention(cohort.retention)}</span>
              <span className="vci-stat-note">of MMR, actually paid</span>
            </div>
          </div>

          {floorGapPct != null && (
            <p className={`vci-verdict ${floorGapPct > 0 ? 'vci-verdict--high' : 'vci-verdict--ok'}`}>
              {floorGapPct > 0
                ? `Floor is ${Math.round(floorGapPct)}% above what this cohort's buyers have been paying.`
                : `Floor is ${Math.abs(Math.round(floorGapPct))}% below the cohort's average paid.`}
            </p>
          )}
        </section>
      )}

      {show('channels') && channels.length > 0 && (
        <section className="vci-section">
          <h4 className="vci-heading">Channel fit</h4>
          <div className="vci-chips">
            {channels.map((c) => (
              <div key={c.channel || 'unknown'} className="vci-chip">
                <span className="vci-chip-name">{c.channel || 'Unknown'}</span>
                <span className="vci-chip-stat">{fmtInt(c.buyers)} buyers</span>
                <span className="vci-chip-stat">{fmtInt(c.buys)} buys</span>
                <span className="vci-chip-stat">{fmtRetention(c.retention)} of MMR</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {show('venues') && venues.length > 0 && (
        <section className="vci-section">
          <h4 className="vci-heading">
            Where to list it
            <span className="vci-subtle"> · top {venues.length} venues by buyer depth</span>
          </h4>
          <p className="vci-subtle vci-venue-note">
            {data?.distance_origin
              ? `Distance is straight-line miles from ${
                  data.distance_origin.kind === 'vehicle_location'
                    ? `where the vehicle is (${data.distance_origin.label})`
                    : `the ${data.distance_origin.label} (the listing has no vehicle location)`
                }.`
              : 'Distance unavailable: the listing has no vehicle location or ZIP.'}
            {' '}Buyers bought or bid in this cohort at the venue; avg paid and retention are weighted by purchases.
          </p>
          <div className="vci-table-wrap">
            <table className="vci-table">
              <thead>
                <tr>
                  <th>Venue</th>
                  <th>Channel</th>
                  <th className="vci-num">Buyers</th>
                  <th className="vci-num">Buys</th>
                  <th className="vci-num">Avg paid</th>
                  <th className="vci-num">Retention</th>
                  <th className="vci-num">Distance</th>
                </tr>
              </thead>
              <tbody>
                {venues.map((v, i) => {
                  const isCurrent =
                    !!currentAuction &&
                    !!v.auction &&
                    v.auction.trim().toUpperCase() === currentAuction.trim().toUpperCase();
                  return (
                    <tr
                      key={`${v.auction}-${v.channel}-${i}`}
                      className={isCurrent ? 'vci-row--current' : undefined}
                    >
                      <td>
                        {v.auction || '–'}
                        {v.state && <span className="vci-subtle"> · {v.state}</span>}
                        {isCurrent && <span className="vci-pill">Listed here</span>}
                      </td>
                      <td>{v.channel || '–'}</td>
                      <td className="vci-num">{fmtInt(v.buyers)}</td>
                      <td className="vci-num">{fmtInt(v.buys)}</td>
                      <td className="vci-num">{fmtMoney(v.avg_paid)}</td>
                      <td className="vci-num">{fmtRetention(v.retention)}</td>
                      <td className="vci-num">{fmtDistance(v.distance_miles)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default VinCohortInsightsCard;
